import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import type { AdminPermissions, UserModules } from '@/api/types';
import {
  getAccessibleSections,
  getAccessibleRecognitionAdminSections,
  type AdminSection,
  type ProductModule,
  type RecognitionAdminSection,
} from '@/auth/accessMatrix';
import type { UserRole } from '@/hooks/useCurrentUser';
import { useCurrentUser, VALID_ROLES } from '@/hooks/useCurrentUser';
import { usePlatform } from '@/hooks/usePlatform';
import { USE_MOCK } from '@/api/client';
import { MOCK_USERS } from '@/data/mockUsers';
import { SUPPORTED_LANGUAGE_CODES } from '@/i18n/languages';
import { toNaturalName } from '@/utils/strings';

export interface DemoUser {
  nombre: string;
  correo: string;
  /** AAD Object ID simulado — GUID estable generado una vez y guardado en localStorage. */
  userId: string;
  /**
   * CLAVE de la unidad organizacional (`mexico`, `europa`…), no su etiqueta. Es lo que viaja a
   * las APIs (`?ou=`, `?ouId=`) y lo que se compara contra `useGdprOuKey()`. Para mostrarla en
   * pantalla se usa `ouNombre`.
   */
  ou: string;
  /** Nombre de la OU para mostrar ("México", "Europa"). Nunca se manda a una API. */
  ouNombre: string;
  do_: string;
  bu: string;
  departamento: string;
  puesto: string;
  numeronomina: string;
  fechaNacimiento: string;
  tenant: 'LATAM' | 'Europa';
  role: UserRole;
  /** Language code the user's Teams/SSO context would provide (es/en/pt/fr/de/nl/ro) */
  idioma: string;
  /**
   * Foto de perfil de AD/Entra ID (Graph, vía UserAdminService). PENDIENTE de backend — ver
   * PENDIENTES.md. Hoy siempre `undefined`: la tarjeta del Perfil cae al avatar de iniciales.
   */
  fotoUrl?: string;
}

// Feature flags — en false oculta la vista para TODOS los roles hasta liberar la fase
export const FEATURES: Record<string, boolean> = {
  executive: false, // Fase 2
};

/**
 * Módulo del producto al que pertenece cada vista del menú principal.
 *
 * `admin` queda fuera desde la Fase 4: el panel dejó de ser solo de Pulso — hospeda
 * además el bloque "Fundamentales". El módulo se comprueba por bloque dentro de
 * `canAccessView`, no sobre la vista entera, para no cerrar el panel a quien solo administra
 * uno de los dos.
 */
const VIEW_MODULE: Record<string, ProductModule> = {
  survey:    'pulso',
  safe:      'pulso',
  executive: 'pulso',
};

/** Roles que dan acceso a cada vista del menú principal, antes de aplicar módulos y permisos. */
export const VIEW_ACCESS: Record<string, UserRole[]> = {
  survey:    ['Employee', 'AdminOU', 'AdminGlobal', 'AdminPulso', 'AdminBrillamos', 'AdminOUBrillamos'],
  safe:      ['Employee', 'AdminOU', 'AdminGlobal', 'AdminPulso', 'AdminBrillamos', 'AdminOUBrillamos'],
  // Los tres roles de Brillamos entran desde la Fase 4: el panel hospeda su administración.
  admin:     ['AdminOU', 'AdminGlobal', 'AdminPulso', 'AdminBrillamos', 'AdminOUBrillamos'],
  executive: ['AdminOU', 'AdminGlobal', 'AdminPulso'],
  // 2026-10-29: `users` («Administradores») dejó de ser vista del menú principal; es la sección
  // `administradores` del panel (grupo Admin General), solo para AdminGlobal — ver accessMatrix.
};

/**
 * Roles con acceso a las entradas del Programa de Reconocimiento. Tabla independiente de
 * VIEW_ACCESS: son entradas del módulo de Reconocimiento, no vistas del portal de Pulso, y
 * todavía no están enlazadas desde ningún menú (ver AppRouter). El backend revalida siempre.
 */
// Catálogo aprobado por el cliente (30-jul-2026). La condición de Europa dejó de ser un rol:
// se deriva de la unidad organizacional del usuario (OrganisationalUnit.Gdpr).
const RECOGNITION_GENERAL_ROLES: UserRole[] = [
  'Employee', 'Manager', 'AdminOU', 'AdminPulso',
  'AdminOUBrillamos', 'AdminBrillamos', 'AdminGlobal',
];

export const RECOGNITION_ACCESS: Record<string, UserRole[]> = {
  reconocer:     RECOGNITION_GENERAL_ROLES,
  brillamos:     RECOGNITION_GENERAL_ROLES,
  perfil:        RECOGNITION_GENERAL_ROLES,
  // Campaña personalizada — igual que el demo: solo Admin Global.
  innovacion:    ['AdminGlobal'],
};

export function canAccessRecognitionEntry(entryId: string, role: UserRole): boolean {
  return (RECOGNITION_ACCESS[entryId] ?? ['AdminGlobal']).includes(role);
}

/**
 * Roles que ADMINISTRAN el Programa de Reconocimiento, según el catálogo de seis roles aprobado
 * por el cliente el 30-jul-2026 (`PMP.SharedContracts/Enumerations/UserRole.cs`).
 *
 * `AdminOU` NO está aquí a propósito: en el catálogo nuevo es "Admin OU **Pulso**", y las
 * atribuciones que el módulo de Reconocimiento repartía entre `AdminFundamentales`,
 * `AdminLocalPais` y `Supervisor` se consolidaron en `AdminBrillamos` / `AdminOUBrillamos`.
 *
 * Fuente única: no duplicar esta lista ni escribir comparaciones sueltas contra roles concretos
 * en las vistas (misma regla que `hasOuScopedAdminRole`, D-040 en `docs/contexto/decisiones.md`).
 */
export const RECOGNITION_ADMIN_ROLES: readonly UserRole[] = [
  'AdminGlobal', 'AdminBrillamos', 'AdminOUBrillamos',
];

/** True si alguno de los roles del usuario administra el Programa de Reconocimiento. */
export function isRecognitionAdmin(roles: readonly UserRole[]): boolean {
  return roles.some((role) => RECOGNITION_ADMIN_ROLES.includes(role));
}

/**
 * Vistas sin restricción de rol: cualquier rol, actual o futuro, las ve. Se declaran aquí
 * en vez de enumerarse en VIEW_ACCESS para que agregar un rol nuevo no obligue a editar
 * este archivo.
 */
export const UNIVERSAL_VIEWS: readonly string[] = ['fundamentals'];

/** Orden en que se listan las secciones del panel de administración. El agrupado visual (Admin
 *  General / Pulso) lo decide `useAdminMenuSections`; aquí solo importa que estén todas. */
export const SECTION_ORDER: AdminSection[] = [
  'catalogoFundamentales', 'catalogoOus',
  'surveys', 'templates', 'analytics', 'rules', 'notifications', 'rolesPulso',
  'system', 'monitor', 'trace', 'jobs', 'gestionUsuarios', 'administradores',
];

/**
 * Orden del bloque "Fundamentales" del panel, idéntico al de la maqueta
 * (`fundamentales-admin.js:6-19`). `views/recognition/admin/sections.tsx` declara los mismos
 * slugs con su icono y su clave i18n; aquí solo vive el orden, para que el gating no dependa de
 * la capa de vistas.
 */
export const RECOGNITION_SECTION_ORDER: RecognitionAdminSection[] = [
  'roles', 'puntos', 'moderacion', 'ganadores', 'campanas',
  'reporteria', 'gestion', 'notificaciones', 'vistas', 'documentacion', 'recap',
];

/** Secciones del panel visibles para los roles y permisos dados, en el orden declarado.
 *  `recognitionPermissions` (opcional) permite que los catálogos maestros del grupo Admin General
 *  se concedan a admins de Brillamos por clave canónica — ver `CATALOG_SECTION_PERMISSION`. */
export function getAccessibleAdminSections(
  roles: readonly UserRole[],
  permissions: AdminPermissions,
  recognitionPermissions: readonly string[] | null = null,
): AdminSection[] {
  return getAccessibleSections(SECTION_ORDER, roles, permissions, recognitionPermissions);
}

/**
 * Secciones del bloque "Fundamentales" visibles para los roles y el permiso efectivo dados, en
 * el orden declarado. `permissions === null` mientras el perfil no se ha resuelto aún — ver
 * `canAccessRecognitionAdminSection` en `accessMatrix.ts`.
 */
export function getAccessibleRecognitionSections(
  roles: readonly UserRole[],
  permissions: readonly string[] | null,
): RecognitionAdminSection[] {
  return getAccessibleRecognitionAdminSections(RECOGNITION_SECTION_ORDER, roles, permissions);
}

/**
 * Visibilidad de una vista del menú principal. Se exige, en este orden: que la fase esté
 * liberada, que el usuario tenga habilitado el módulo al que pertenece la vista, que la vista no
 * sea universal (esas las ve cualquier rol), que alguno de sus roles la contemple y —para `admin`—
 * que además tenga algo que hacer dentro.
 *
 * El módulo se comprueba ANTES de `UNIVERSAL_VIEWS` a propósito: "universal" significa sin
 * restricción de ROL, no sin restricción de módulo. Con el orden inverso, declarar universal una
 * vista que pertenece a un módulo la volvía visible para quien no tiene ese módulo habilitado.
 *
 * `recognitionPermissions` es el permiso efectivo del módulo Reconocimiento (null mientras el
 * perfil no se ha resuelto) — decide, junto con el rol, si el bloque "Fundamentales" del panel
 * tiene alguna sección que mostrar (ver `getAccessibleRecognitionSections`).
 */
export function canAccessView(
  view: string,
  roles: readonly UserRole[],
  permissions: AdminPermissions,
  modules: UserModules,
  recognitionPermissions: readonly string[] | null,
): boolean {
  if (FEATURES[view] === false) return false;

  const requiredModule = VIEW_MODULE[view];
  if (requiredModule === 'pulso' && !modules.canViewPulso) return false;
  if (requiredModule === 'brillamos' && !modules.canViewBrillamos) return false;

  if (UNIVERSAL_VIEWS.includes(view)) return true;

  const allowedRoles = VIEW_ACCESS[view] ?? ['AdminGlobal'];
  if (!roles.some(role => allowedRoles.includes(role))) return false;

  // El panel hospeda dos bloques y basta con tener algo que hacer en UNO. El módulo Pulso se
  // exige solo para el bloque de Pulso (no está en VIEW_MODULE por eso); el de Fundamentales se
  // resuelve por permiso efectivo (o rol, en su defecto) — ver `getAccessibleRecognitionSections`.
  if (view === 'admin') {
    const hasPulsoSections = modules.canViewPulso
      && getAccessibleAdminSections(roles, permissions, recognitionPermissions).length > 0;
    return hasPulsoSections || getAccessibleRecognitionSections(roles, recognitionPermissions).length > 0;
  }
  return true;
}

function generateUserId(): string {
  // Genera un GUID v4 estable que imita un AAD Object ID.
  //
  // Usa el generador criptográfico del navegador y no `Math.random()`. No es que este id
  // necesite ser impredecible —identifica al usuario de demo y nada más—, sino que
  // `Math.random()` aquí disparaba 8 alertas «alta» de CodeQL (js/insecure-randomness) que
  // inflaban el tablero de seguridad y tapaban los hallazgos que sí importan.
  //
  // Se arma con `getRandomValues` en vez del más corto `crypto.randomUUID()` a propósito:
  // esta función se evalúa al IMPORTAR el módulo (DEFAULT_USER / DEFAULT_SLOTS), y
  // `randomUUID` solo existe en contextos seguros. Servido desde un origen `http://` sería
  // `undefined` y la excepción tumbaría la carga del módulo entero. `getRandomValues` está
  // disponible también fuera de contexto seguro.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // versión 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variante RFC 4122
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const DEFAULT_USER: DemoUser = {
  nombre: '',
  correo: '',
  userId: generateUserId(),
  ou: '',
  ouNombre: '',
  do_: '',
  bu: '',
  departamento: '',
  puesto: '',
  numeronomina: '',
  fechaNacimiento: '',
  tenant: 'LATAM',
  role: 'Employee',
  idioma: 'es',
};

const STORAGE_KEY = 'sigma-demo-user';

const VALID_TENANTS: DemoUser['tenant'][] = ['LATAM', 'Europa'];

const SUPPORTED_UI_LANGUAGES = SUPPORTED_LANGUAGE_CODES;

/**
 * Precedencia del idioma inicial de un usuario real (MSAL): el locale de Teams gana siempre que
 * sea uno de los 7 idiomas soportados (requerimiento: dentro de Teams manda el idioma que el
 * usuario ya tiene configurado ahí, no el heredado de Azure AD al alta); si no viene o no es
 * soportado (fuera de Teams, o Teams en un idioma que no tenemos), se usa el preferredLanguage
 * del perfil backend; si tampoco, el idioma del navegador; si tampoco, español.
 *
 * El último eslabón era inglés y hacía que la aplicación arrancara en ENG donde el prototipo
 * arranca en ESP. Se conserva el eslabón de navegador a propósito: el tenant Europa depende de
 * esa detección para nl/de/fr/ro cuando no hay Teams. Ver docs/contexto/decisiones.md.
 */
export function resolveInitialLanguage(
  preferredLanguage: string | undefined,
  teamsLocale: string | undefined,
  browserLanguage: string,
): string {
  const normalizedTeamsLang = (teamsLocale ?? '').split('-')[0];
  if (SUPPORTED_UI_LANGUAGES.includes(normalizedTeamsLang)) {
    return normalizedTeamsLang;
  }
  if (preferredLanguage && SUPPORTED_UI_LANGUAGES.includes(preferredLanguage)) {
    return preferredLanguage;
  }
  const normalizedBrowserLang = browserLanguage.split('-')[0];
  if (SUPPORTED_UI_LANGUAGES.includes(normalizedBrowserLang)) {
    return normalizedBrowserLang;
  }
  return 'es';
}

/** Campo de texto del usuario guardado, o su valor por omisión si no es una cadena. */
function storedText(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function sanitizeStoredUser(raw: unknown): DemoUser {
  if (typeof raw !== 'object' || raw === null) return { ...DEFAULT_USER };
  const u = raw as Record<string, unknown>;
  const storedUserId = storedText(u['userId'], '');
  return {
    ...DEFAULT_USER,
    // P3-4 — tercera y última frontera por la que entra un nombre a la app: el usuario demo
    // guardado. Las otras dos (`useCurrentUser` para la identidad propia y `toPersonSummary` para
    // las demás personas) ya aplican la misma regla. Es idempotente: sobre un nombre ya natural no
    // hace nada, así que no importa por cuál de las tres haya entrado.
    nombre:          toNaturalName(storedText(u['nombre'], DEFAULT_USER.nombre)),
    correo:          storedText(u['correo'], DEFAULT_USER.correo),
    userId:          storedUserId || generateUserId(),
    ou:              storedText(u['ou'], DEFAULT_USER.ou),
    ouNombre:        storedText(u['ouNombre'], DEFAULT_USER.ouNombre),
    do_:             storedText(u['do_'], DEFAULT_USER.do_),
    bu:              storedText(u['bu'], DEFAULT_USER.bu),
    departamento:    storedText(u['departamento'], DEFAULT_USER.departamento),
    puesto:          storedText(u['puesto'], DEFAULT_USER.puesto),
    numeronomina:    storedText(u['numeronomina'], DEFAULT_USER.numeronomina),
    fechaNacimiento: storedText(u['fechaNacimiento'], DEFAULT_USER.fechaNacimiento),
    tenant:         VALID_TENANTS.includes(u['tenant'] as DemoUser['tenant']) ? u['tenant'] as DemoUser['tenant'] : DEFAULT_USER.tenant,
    role:           VALID_ROLES.includes(u['role'] as UserRole)               ? u['role'] as UserRole             : DEFAULT_USER.role,
    idioma:          storedText(u['idioma'], DEFAULT_USER.idioma),
    fotoUrl:         typeof u['fotoUrl'] === 'string' ? u['fotoUrl'] : undefined,
  };
}

function loadFromStorage(): DemoUser {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_USER };
    return sanitizeStoredUser(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_USER };
  }
}

interface UserContextValue {
  demoUser: DemoUser;
  setDemoUser: (u: DemoUser) => void;
  /** Returns detected UserRole if email matches a mock user, null otherwise */
  detectRoleByEmail: (email: string) => UserRole | null;
  /** Returns the full mock user record if email matches */
  findMockUser: (email: string) => typeof MOCK_USERS[number] | undefined;
}

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: ReactNode }) {
  const [demoUser, setDemoUserState] = useState<DemoUser>(() => loadFromStorage());
  const currentUser = useCurrentUser();
  const { teamsContext } = usePlatform();
  // QA (2026-08-28) — bug reportado: el idioma (y con él, cualquier arte por idioma como el logo de
  // Brillamos) volvía solo a español unos instantes después de cambiarlo desde `LanguageMenu`. Causa
  // real: `currentUser` es un objeto MEMOIZADO con muchas dependencias propias (permisos, alcance de
  // OU, estado del perfil...) que cambian de referencia por motivos AJENOS al idioma — cada vez que
  // eso pasa, este efecto se repite y `resolveInitialLanguage` vuelve a leer `currentUser.preferredLanguage`,
  // que es un `useState` de `useCurrentUser` fijado UNA sola vez con la respuesta original del backend
  // y que `LanguageMenu` nunca actualiza ahí (solo persiste el cambio en el servidor y en `demoUser`
  // local) — así que cualquier re-render disparaba `idioma` de vuelta al valor viejo.
  //
  // Este ref limita `resolveInitialLanguage` a la PRIMERA resolución "definitiva" por identidad (de
  // ahí su nombre) — y NO simplemente a la primera vez que se ve el `id`. `currentUser.id` llega de
  // MSAL/Teams de inmediato, pero `currentUser.preferredLanguage` es OTRO `useState` de
  // `useCurrentUser` que solo se llena después de que resuelve el GET del perfil (asíncrono): hay una
  // ventana real donde `currentUser` ya no es null (id presente) pero `preferredLanguage` todavía es
  // `undefined`. Si se recalculara solo por `id`, ese primer cálculo (con `preferredLanguage`
  // ausente) se congelaría para siempre y el idioma guardado en el perfil real nunca llegaría a
  // aplicarse. Por eso se guarda también si el PERFIL ya estaba resuelto (`profileResolved`) la
  // última vez: se recalcula de nuevo la primera vez que `profileResolved` pasa a `true` para este
  // usuario (ahí es cuando `preferredLanguage` por fin tiene el valor real), y a partir de ahí se
  // conserva `prev.idioma` — la elección vigente del usuario, sea la del backend o la que acaba de
  // tocar en el menú — en vez de recalcularla en cada re-render posterior.
  const idiomaResueltoRef = useRef<{ userId: string; profileResolved: boolean } | null>(null);

  useEffect(() => {
    if (USE_MOCK || !currentUser) return;
    // MSAL real: la identidad de Azure AD SIEMPRE manda (sobreescribe cualquier demo user previo
    // guardado en localStorage). userId = oid real; los roles vienen de la BD. `demoUser.role`
    // guarda el rol de mayor privilegio SOLO como etiqueta del perfil simulado: el acceso lo
    // decide la lista completa `currentUser.roles` (ver canAccessView).
    const anterior = idiomaResueltoRef.current;
    const esUsuarioNuevo = !anterior || anterior.userId !== currentUser.id;
    const esPrimeraResolucion =
      esUsuarioNuevo || (!anterior.profileResolved && currentUser.profileResolved);
    setDemoUserState(anterior => {
      // Cambio de CUENTA (fila 6): `sigma-demo-user` es una llave compartida por todas las cuentas del mismo
      // navegador, y abajo los campos se rellenan con `currentUser.xxx || prev.xxx`. Sin esto, una cuenta sin
      // foto, puesto, departamento u OU heredaba los de la anterior (la foto de A seguía viéndose en la sesión
      // de B). Solo se limpiaba al cerrar sesión con el botón; no al cambiar de cuenta por otro camino (sesión
      // expirada, «usar otra cuenta» de Microsoft, otra sesión Frontline). Se parte de los datos vacíos de
      // identidad cuando el id cambia; el idioma y las preferencias locales se conservan.
      const cambioDeCuenta = Boolean(anterior.userId) && Boolean(currentUser.id) && anterior.userId !== currentUser.id;
      const prev: DemoUser = cambioDeCuenta
        ? {
            ...anterior,
            nombre: '', correo: '', ou: '', ouNombre: '', do_: '', bu: '', departamento: '', puesto: '',
            numeronomina: '', fechaNacimiento: '', fotoUrl: undefined, tenant: DEFAULT_USER.tenant,
          }
        : anterior;
      const updated: DemoUser = {
        ...prev,
        userId: currentUser.id || prev.userId,
        nombre: currentUser.displayName || prev.nombre,
        correo: currentUser.email || prev.correo,
        // P3-2 — identidad organizacional del directorio. Sin estas cuatro líneas la tarjeta del
        // Perfil se queda sin puesto, sin chip de área y sin chip de OU aunque el UserAdminService
        // los devuelva: los `&&` de PerfilView no renderizan sobre cadena vacía. Además `ou` es la
        // clave que necesitan la campaña activa (useActiveCampaign) y el alcance GDPR, así que sin
        // ella el banner de Brillamos y la tercera pestaña tampoco aparecían nunca.
        ou: currentUser.ouId || prev.ou,
        ouNombre: currentUser.ouName || prev.ouNombre,
        puesto: currentUser.jobTitle || prev.puesto,
        departamento: currentUser.department || prev.departamento,
        fotoUrl: currentUser.photoUrl || prev.fotoUrl,
        // NOTA (Task 11 audit): este 'europa' NO es un slug de OU del catálogo (OU_CATALOG/useOUs) —
        // es el heurístico para resolver a qué TENANT de backend ('LATAM' | 'Europa', dos despliegues
        // distintos) pertenece el usuario real de MSAL, a partir de su dominio de correo. useOUs()
        // necesita `demoUser.tenant` para poder pedir las OUs (getOUs(tenantId)), así que no se puede
        // sustituir por `useGdprOuKey()` aquí sin crear una dependencia circular. Fuera de alcance de
        // este plan de refactor de OUs; queda documentado para no confundirlo con el literal 'europa'
        // de OU ya corregido en useOUs.ts/Task 7.
        tenant: currentUser.email?.toLowerCase().includes('europa') ? 'Europa' : prev.tenant,
        role: currentUser.primaryRole,
        idioma: esPrimeraResolucion
          ? resolveInitialLanguage(currentUser.preferredLanguage, teamsContext?.locale, navigator.language || '')
          : prev.idioma,
      };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch { /* noop */ }
      return updated;
    });
    idiomaResueltoRef.current = { userId: currentUser.id, profileResolved: currentUser.profileResolved };
  }, [currentUser, teamsContext]);

  function setDemoUser(u: DemoUser): void {
    // Misma regla de nombre que al hidratar desde el almacenamiento (P3-4): quien lo escriba a
    // mano en el formulario del usuario demo lo verá igual que si viniera del directorio.
    const normalized: DemoUser = { ...u, nombre: toNaturalName(u.nombre) };
    setDemoUserState(normalized);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch { /* storage unavailable in this environment */ }
  }

  function detectRoleByEmail(email: string): UserRole | null {
    const match = findMockUser(email);
    if (!match) return null;
    // El catálogo de mocks ya guarda identificadores de rol del backend: se toma el primero,
    // que es el que el simulador de demo muestra como etiqueta del perfil.
    return match.roles[0] ?? 'Employee';
  }

  function findMockUser(email: string) {
    return MOCK_USERS.find(
      mockUser => mockUser.email?.toLowerCase() === email.toLowerCase().trim(),
    );
  }

  return (
    <UserContext.Provider value={{ demoUser, setDemoUser, detectRoleByEmail, findMockUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUserContext must be used within <UserProvider>');
  return ctx;
}
